import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, ChevronUp, ChevronDown, BookOpen, Save, X,
} from "lucide-react";

export default function SubjectsPanel() {
  const [subjects, setSubjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [newSubject, setNewSubject] = useState({ code: "", name: "", description: "", order: 0 });
  const [newChapter, setNewChapter] = useState("");
  const [editingChapter, setEditingChapter] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [confirm, setConfirm] = useState(null); // {type, item}

  const loadSubjects = () => api.get("/subjects").then((r) => {
    setSubjects(r.data);
    if (!selectedId && r.data.length) setSelectedId(r.data[0].id);
  });
  const loadChapters = (sid) => {
    if (!sid) { setChapters([]); return; }
    api.get(`/topics?subject_id=${sid}`).then((r) => setChapters(r.data));
  };
  useEffect(() => { loadSubjects(); }, []);
  useEffect(() => { loadChapters(selectedId); /* eslint-disable-next-line */ }, [selectedId]);

  const selected = subjects.find((s) => s.id === selectedId);

  const addSubject = async () => {
    try {
      await api.post("/subjects", newSubject);
      toast.success("Subject added");
      setNewSubject({ code: "", name: "", description: "", order: 0 });
      loadSubjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const addChapter = async () => {
    if (!newChapter.trim() || !selectedId) return;
    try {
      await api.post("/topics", { subject_id: selectedId, name: newChapter.trim(), order: chapters.length });
      toast.success("Chapter added");
      setNewChapter("");
      loadChapters(selectedId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveRename = async () => {
    if (!editingName.trim()) return;
    try {
      await api.put(`/topics/${editingChapter.id}`, { name: editingName.trim() });
      toast.success("Renamed");
      setEditingChapter(null); setEditingName("");
      loadChapters(selectedId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const moveChapter = async (idx, delta) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= chapters.length) return;
    const next = [...chapters];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    const orders = next.map((c, i) => ({ id: c.id, order: i }));
    setChapters(next.map((c, i) => ({ ...c, order: i })));
    try {
      await api.post("/topics/reorder", { orders });
    } catch (e) {
      toast.error("Reorder failed");
      loadChapters(selectedId);
    }
  };

  const deleteChapter = async (ch, cascade) => {
    try {
      await api.delete(`/topics/${ch.id}${cascade ? "?cascade=true" : ""}`);
      toast.success(cascade ? `Chapter and ${ch.question_count} questions deleted` : "Chapter deleted");
      setConfirm(null);
      loadChapters(selectedId);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg);
      if (e.response?.status === 409) setConfirm({ type: "chapter-force", item: ch });
    }
  };

  const deleteSubject = async (s) => {
    try {
      await api.delete(`/subjects/${s.id}`);
      toast.success("Subject deleted");
      setConfirm(null);
      setSelectedId(null);
      loadSubjects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      {/* Subjects column */}
      <Card className="border-border/70">
        <CardContent className="p-5">
          <div className="mb-3 font-display text-base font-bold">Subjects</div>
          <div className="space-y-1">
            {subjects.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                className={`group flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                  s.id === selectedId
                    ? "border-[hsl(var(--brand-navy))] bg-[hsl(var(--brand-navy))]/5"
                    : "border-border hover:bg-secondary"
                }`}
                data-testid={`subject-row-${s.code}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.code} · {s.question_count} Qs · {s.topic_count} chapters</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirm({ type: "subject", item: s }); }}
                  className="ml-2 rounded p-1 opacity-0 transition hover:bg-rose-100 group-hover:opacity-100"
                  data-testid={`del-subject-${s.code}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </button>
              </button>
            ))}
          </div>
          <div className="mt-5 space-y-2 border-t border-border pt-5">
            <div className="font-display text-sm font-bold">Add subject</div>
            <Input placeholder="Code (e.g. GEO)" value={newSubject.code} onChange={(e) => setNewSubject({ ...newSubject, code: e.target.value })} data-testid="new-subject-code" />
            <Input placeholder="Name" value={newSubject.name} onChange={(e) => setNewSubject({ ...newSubject, name: e.target.value })} data-testid="new-subject-name" />
            <Textarea placeholder="Description" rows={2} value={newSubject.description} onChange={(e) => setNewSubject({ ...newSubject, description: e.target.value })} data-testid="new-subject-desc" />
            <Button onClick={addSubject} className="w-full bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-subject-save">
              <Plus className="mr-2 h-4 w-4" /> Add subject
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Chapters column */}
      <Card className="border-border/70">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Chapters in</div>
              <div className="font-display text-xl font-bold">{selected?.name || "Select a subject"}</div>
              {selected?.description && <div className="mt-1 max-w-xl text-sm text-muted-foreground">{selected.description}</div>}
            </div>
            <div className="flex-none text-right">
              <div className="font-display text-2xl font-bold">{chapters.length}</div>
              <div className="text-xs text-muted-foreground">chapters</div>
            </div>
          </div>

          {selected && (
            <>
              <div className="mt-5 flex gap-2">
                <Input
                  placeholder="New chapter name (e.g. Basics of Navigation)"
                  value={newChapter}
                  onChange={(e) => setNewChapter(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addChapter(); }}
                  data-testid="new-chapter-input"
                />
                <Button onClick={addChapter} disabled={!newChapter.trim()} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-chapter-save">
                  <Plus className="mr-2 h-4 w-4" /> Add chapter
                </Button>
              </div>

              <div className="mt-5 overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead className="w-24">Questions</TableHead>
                    <TableHead className="w-48 text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {chapters.map((c, i) => (
                      <TableRow key={c.id} data-testid={`chapter-row-${c.id}`}>
                        <TableCell className="font-mono text-xs text-muted-foreground">{i + 1}</TableCell>
                        <TableCell>
                          {editingChapter?.id === c.id ? (
                            <div className="flex gap-2">
                              <Input
                                autoFocus
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditingChapter(null); }}
                                data-testid={`rename-input-${c.id}`}
                              />
                              <Button size="icon" variant="ghost" onClick={saveRename} data-testid={`save-rename-${c.id}`}><Save className="h-4 w-4 text-emerald-600" /></Button>
                              <Button size="icon" variant="ghost" onClick={() => setEditingChapter(null)}><X className="h-4 w-4" /></Button>
                            </div>
                          ) : (
                            <div className="font-medium">{c.name}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{c.question_count}</TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => moveChapter(i, -1)} disabled={i === 0} data-testid={`move-up-${c.id}`}><ChevronUp className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => moveChapter(i, 1)} disabled={i === chapters.length - 1} data-testid={`move-down-${c.id}`}><ChevronDown className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => { setEditingChapter(c); setEditingName(c.name); }} data-testid={`rename-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setConfirm({ type: "chapter", item: c })} data-testid={`del-chapter-${c.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {chapters.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="p-10 text-center text-sm text-muted-foreground">
                          <BookOpen className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
                          No chapters yet. Add your first chapter above.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmations */}
      <AlertDialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <AlertDialogContent>
          {confirm?.type === "subject" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete subject "{confirm.item.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete the subject, {confirm.item.topic_count} chapters, and {confirm.item.question_count} questions. Student attempt history is preserved. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteSubject(confirm.item)} className="bg-rose-600 hover:bg-rose-700">Delete subject</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirm?.type === "chapter" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete chapter "{confirm.item.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  {confirm.item.question_count === 0
                    ? "This chapter is empty. It will be deleted."
                    : `This chapter has ${confirm.item.question_count} questions. Deletion will be blocked unless you choose to delete them too.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteChapter(confirm.item, false)} className="bg-rose-600 hover:bg-rose-700" data-testid="chapter-delete-safe">Delete chapter</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirm?.type === "chapter-force" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Also delete {confirm.item.question_count} questions?</AlertDialogTitle>
                <AlertDialogDescription>
                  To delete this chapter, its {confirm.item.question_count} questions must be removed. Student history for those questions is preserved. Continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteChapter(confirm.item, true)} className="bg-rose-600 hover:bg-rose-700" data-testid="chapter-delete-force">Delete chapter + questions</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
